import { Link } from 'react-router-dom';
import { ArrowRight, Target, Lightbulb, TrendingUp, Check } from 'lucide-react';
import { TrustBar } from '../components/TrustBar';

export function CareerClarityJournalPage() {
  return (
    <div className="bg-white">
      <section className="relative bg-gradient-to-b from-[#FDF8F4] to-white pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
            <Target className="h-4 w-4" />
            <span>For People Feeling Behind in Their Career</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 leading-tight">
            Career Clarity Journal
            <span className="block text-coral-500">Find your next move in 5 minutes a day</span>
          </h1>

          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Stop spinning your wheels. Get clear on what you actually want from your career and create a plan to get there.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-full hover:bg-slate-800 transition-all shadow-lg"
            >
              Start free - get your first win
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>

          <TrustBar className="mb-8" />

          <p className="text-sm text-slate-500">
            Free plan: 3 coaching sessions/week + 10 journal entries/month
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Built for career transitions and tough decisions
            </h2>
            <p className="text-xl text-slate-600">
              Not generic journaling. Specific tools for career clarity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Situation Selector',
                description: 'Pick your challenge: job change, career pivot, negotiation, or feeling stuck'
              },
              {
                icon: Lightbulb,
                title: 'Structured Output',
                description: 'Get 2 clear options, 3 next steps, and a reflection prompt for tomorrow'
              },
              {
                icon: TrendingUp,
                title: 'Track Progress',
                description: 'See patterns over time. Notice what actually moves you forward.'
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-50 to-sage-50 rounded-3xl p-12 border border-slate-200">
            <h2 className="text-3xl font-heading font-bold text-slate-900 mb-8">
              Common career questions VZMO helps with:
            </h2>

            <div className="space-y-4">
              {[
                'Should I take this job offer or stay where I am?',
                'Is it too late to change careers?',
                'How do I know what I actually want to do?',
                'Am I being paid fairly?',
                'Should I ask for a promotion or look elsewhere?',
                'How do I stop feeling behind my peers?'
              ].map((question, index) => (
                <div key={index} className="flex items-start gap-3 bg-white rounded-xl p-4">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{question}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            See your first career clarity win in 30 seconds
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Free to start. No credit card. Get immediate clarity on your next career move.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-white text-slate-900 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all shadow-lg"
          >
            Start your career clarity journal
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
